<script setup>
import Select from "./components/form-fields/Select.vue";
import ColorPicker from "./components/form-fields/ColorPicker.vue";
import Sides from "./components/form-fields/Sides.vue";
import Quantity from "./components/form-fields/Quantity.vue";
import MetreOrRoll from "./components/form-fields/MetreOrRoll.vue";
import {computed, nextTick, onBeforeMount, ref, useTemplateRef, watch} from "vue";
import ActionWithInput from "./components/form-fields/ActionWithInput.vue";
import isObject from "../utilities/is-object";
import Message from "./components/messages/Message.vue";
import OutOfStock from "./components/messages/OutOfStock.vue";
import Backordered from "./components/messages/Backordered.vue";
import debounce from 'lodash-es/debounce';

const props = defineProps({
    productId: null,
    apiBaseUrl: null,
    cartUrl: null,
    contactUrl: null,
    dataLayer: {},
})

let product = ref({})
let quantity = ref(1)
let error = ref(null)
let fatalError = ref(null)
let stockStatus = ref(null)
let priceExVat = ref(null)
let priceExVatFormatted = ref(null)
let priceIncVat = ref(null)
let priceIncVatFormatted = ref(null)
let confirmation = ref(false)
let confirmedOnce = ref(false)
let confirmationMessage = useTemplateRef('confirmationMessage')
let pendingRequest = ref(null)
let debouncedAttributes = computed( () => {
    return {...{quantity: quantity.value}, ...getAttributesForWatcher( true )};
} );
let immediateAttributes = computed( () => getAttributesForWatcher( false ) );
let loading = ref(false);
let wcNonce = ref(null);
let wpNonce = ref(null);

/**
 * Get live WooCommerce nonce.
 *
 * @returns {Promise<string>}
 */
async function getWcNonce() {
    if( wcNonce.value ) {
        return wcNonce.value;
    }
    
    const response = await fetch( `${props.apiBaseUrl}wc/store/v1/cart` );

    return response.headers.get( 'Nonce' );
}

/**
 * Get live WordPress nonce.
 * 
 * @returns {Promise<string>}
 */
async function getWpNonce() {
    if( wpNonce.value ) {
        return wpNonce.value;
    }
    
    const response = await fetch( `${props.apiBaseUrl}whippet/v1/nonce` );
    const json = await response.json();

    return json.nonce ?? null;
}

/**
 * Get product attribute values used by the updatePrice watcher.
 * 
 * @param {Boolean} debounced Get debounced or immediate values.
 * @returns {Object}
 */
function getAttributesForWatcher( debounced ) {
    let attributesForWatcher = {};
    
    for (let attributeIndex in product.value.attributes) {
        const attribute = product.value.attributes[attributeIndex];

        if( debounced === attribute.debounce ) {
            attributesForWatcher[attribute.id] = getAttributeValue( attribute );
        }

        for (let subattributeIndex in attribute.subattributes) {
            const subattribute = attribute.subattributes[subattributeIndex];

            if( debounced === subattribute.debounce ) {
                attributesForWatcher[subattribute.id] = getAttributeValue( subattribute );
            }
        }
    }

    return attributesForWatcher;
}

/**
 * Hide quantity if this option is selected for any attributes.
 */
const hideQuantity = computed( () => {
    for (let attributeIndex in product.value.attributes) {
        const attribute = product.value.attributes[attributeIndex];

        if( isObject( attribute.value ) && attribute.value.hide_quantity ) {
            return true;
        }
    }

    return false;
} );

/**
 * Set up product data and update price on init.
 */
onBeforeMount( async () => {
    product.value = await getProductData();
    setProductAttributesFromURL();
} );

/**
 * If query string is present, set attribute values from URL.
 */
function setProductAttributesFromURL() {
    const params = new URLSearchParams( window.location.search.replace( '&amp;', '&' ) );

    if( params.size > 0 ) {
        params.forEach( (urlAttributeValue, urlAttributeKey) => {
            for( let attributeIndex in product.value.attributes ) {
                const attribute = product.value.attributes[attributeIndex];

                if( attribute.id === urlAttributeKey ) {
                    for( let attributeOptionIndex in attribute.options ) {
                        const attributeOption = attribute.options[attributeOptionIndex];

                        if( attributeOption.id === urlAttributeValue ) {
                            product.value.attributes[attributeIndex].value = attributeOption;
                            
                            break;
                        }
                    }
                    
                    break;
                }
            }
        } );
    }
}

/**
 * Fetch product attributes from the API.
 *
 * Sends a GET request to the product attributes endpoint using the configured
 * `apiBaseUrl`, `apiNonce`, and `productId`. If the response has a successful
 * status code, the method resolves to the parsed JSON body; otherwise it
 * resolves to `undefined`.
 *
 * @return {Promise<Object|undefined>} A promise that resolves with the product
 * data object or `undefined` when the HTTP response is not OK.
 */
async function getProductData() {
    const response = await fetch( `${props.apiBaseUrl}whippet/v1/product-attributes/${props.productId}` );

    if( response.ok ) {
        const json = await response.json();

        if( json ) {
            return json;
        }
    }

    fatalError.value = 'Failed to retrieve product details.';
}

/**
 * Product price attributes, which are sent to WC to calculate product price.
 * 
 * @type {object|null} Null if form is incomplete, otherwise a payload to send to API.
 */
const productPriceAttributes = computed( () => {
    let payload = {
        id: props.productId,
        quantity: productQuantity.value,
        attributes: {},
    };

    for (let attributeIndex in product.value.attributes) {
        const attribute = product.value.attributes[attributeIndex];
        
        if( !isAttributeValid( attribute ) ) {
            return null;
        }

        payload.attributes[attribute.id] = {};

        if( attribute.affects_price ) {
            payload.attributes[attribute.id].value = getAttributeValue( attribute );
        }

        for (let subattributeIndex in attribute.subattributes) {
            const subattribute = attribute.subattributes[subattributeIndex];

            if( !isAttributeValid( subattribute ) ) {
                return null;
            }

            payload.attributes[attribute.id][subattribute.id] = getAttributeValue( subattribute );
        }
    }

    return payload;
} );

/**
 * Product quantity taking into account hide quantity functionality.
 */
const productQuantity = computed( () => {
    return hideQuantity.value ? 1 : quantity.value;
} );

/**
 * Updates the displayed price for the product by sending a POST request to the
 * backend API with the current add‑to‑cart attributes.
 *
 * @return {Promise<void>} Resolves when the price update process has completed
 * or rejects if an unexpected error occurs during the fetch operation.
 */
async function updatePrice() {
    // Cancel any in-flight request or pending debounce timer
    debouncedCalculate.cancel();

    if( pendingRequest.value ) {
        pendingRequest.value.abort();
        pendingRequest.value = null
    }
    
    if( productPriceAttributes.value ) {
        pendingRequest.value = new AbortController();
        loading.value = true;

        // Get live nonce
        wpNonce.value = await getWpNonce();
        
        if( wpNonce.value ) {
            try {
                const response = await fetch( `${props.apiBaseUrl}whippet/v1/product-price/${props.productId}`, {
                    method: 'POST',
                    headers: {
                        'X-WP-Nonce': wpNonce.value,
                        'Content-Type': 'application/json; charset=utf-8',
                    },
                    body: JSON.stringify( productPriceAttributes.value ),
                    signal: pendingRequest.value.signal,
                } );

                if( response.ok ) {
                    const responseJson = await response.json();

                    if( responseJson.price_including_vat && responseJson.price_including_vat_formatted && responseJson.price_excluding_vat && responseJson.price_excluding_vat_formatted && responseJson.stock_status ) {
                        setPriceParameters( responseJson.price_including_vat, responseJson.price_including_vat_formatted, responseJson.price_excluding_vat, responseJson.price_excluding_vat_formatted, responseJson.stock_status );
                    } else {
                        await handleUpdatePriceError( response );
                    }
                } else {
                    await handleUpdatePriceError( response );
                }
            } catch ( e ) {
                setPriceParameters( null, null, null, null, null, 'A network failure occurred - check you\'re still connected to the internet, or contact us for help.' );
            } finally {
                pendingRequest.value = null;
                loading.value = false;
            }
        } else {
            setPriceParameters( null, null, null, null, null, 'Failed to retrieve item price. Try refreshing the page or contact us for help.' );
        }
    } else {
        setPriceParameters();
    }
}

/**
 * Helper function to set various price parameters.
 *
 * @param priceIncVatValue
 * @param priceIncVatFormattedValue
 * @param priceExVatValue
 * @param priceExVatFormattedValue
 * @param stockStatusValue
 * @param errorValue
 */
function setPriceParameters( priceIncVatValue = null, priceIncVatFormattedValue = null, priceExVatValue = null, priceExVatFormattedValue = null, stockStatusValue = null, errorValue = null ) {
    error.value = errorValue;
    stockStatus.value = stockStatusValue;
    priceExVat.value = priceExVatValue;
    priceExVatFormatted.value = priceExVatFormattedValue;
    priceIncVat.value = priceIncVatValue;
    priceIncVatFormatted.value = priceIncVatFormattedValue;
    loading.value = false;
}

/**
 * Reset prices and update error message.
 * 
 * @param {?Response} response
 */
async function handleUpdatePriceError( response = null ) {
    setPriceParameters( null, null, null, null, null, 'Failed to retrieve price.' );
    
    if( response ) {
        const responseJson = await response.json();
        
        if( responseJson && responseJson.message ) {
            error.value += ' ' + responseJson.message;
        }
    }
}

/**
 * Get attributes used in add to cart API call.
 *
 * @returns {object}
 */
function getAddToCartAttributes() {
    let payload = {
        id: props.productId,
        quantity: productQuantity.value,
        variation: [],
        whippet_attributes: [],
    };

    for (let attributeIndex in product.value.attributes) {
        const attribute = product.value.attributes[attributeIndex];

        if( attribute.variation ) {
            payload.variation.push( {
                attribute: attribute.id,
                value: getAttributeValue( attribute ),
            } );
        } else {
            const attributeValue = {
                id: attribute.id,
                value: getAttributeValue( attribute ),
            };
            
            for (let subattributeIndex in attribute.subattributes) {
                const subattribute = attribute.subattributes[subattributeIndex];

                attributeValue[subattribute.id] = getAttributeValue( subattribute );
            }
            
            payload.whippet_attributes.push( attributeValue );
        }
    }
    
    return payload;
}

/**
 * Handle the Add to Cart button - either display a warning pop-up or process request.
 *
 * @returns {Promise<void>}
 */
async function addToCartButtonHandler( force = false ) {
    if( productPriceAttributes.value ) {
        error.value = null;
        
        if( !['whippet_bespoke_seal', 'whippet_metered_seal'].includes( product.value.type ) ) { // Simple and variable products
            await addToCart();
        } else { // Metered and bespoke products
            if( force || confirmedOnce.value ) {
                confirmation.value = false;
                await addToCart();
            } else {
                confirmation.value = true;
                confirmedOnce.value = true;
                await nextTick();
                confirmationMessage.value.scrollIntoView( {
                    block: 'center',
                    inline: 'center',
                } );
            }
        }
    } else {
        error.value = 'Please select all required options.';
    }
}

/**
 * Makes the request to add the current item to cart.
 * 
 * @returns {Promise<void>} A promise that resolves when the add‑to‑cart operation is complete.
 */
async function addToCart() {
    loading.value = true;
    wcNonce.value = await getWcNonce();
    
    if( !wcNonce.value ) {
        error.value = 'Failed to add item to cart. Try refreshing the page or contact us for help.';
        loading.value = false;
        
        return;
    }
    
    const response = await fetch( `${props.apiBaseUrl}wc/store/v1/cart/add-item`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Nonce': wcNonce.value,
            'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify( getAddToCartAttributes() ),
    } );

    if( response.ok ) {
        addToCartDataLayer();
        window.location.href = props.cartUrl;
    } else {
        const responseBody = await response.json();
        
        if( responseBody.message ) {
            error.value = responseBody.message;
        } else {
            error.value = 'Failed to add item to cart.';
        }
    }
    
    loading.value = false;
}

/**
 * Push add_to_cart event to dataLayer.
 */
function addToCartDataLayer() {
    const productDataLayer = JSON.parse( props.dataLayer );
    
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push( {
        event: 'add_to_cart',
        ecommerce: {
            currency: productDataLayer.currency ?? '',
            value: priceExVat.value,
            items: [
                {
                    item_id: productDataLayer.sku,
                    item_name: productDataLayer.title,
                    price: priceExVat.value / quantity.value,
                    quantity: quantity.value,
                },
            ],
        },
    } );
}

/**
 * Format attribute value for use in API calls.
 *
 * @param {*} attribute Product attribute.
 * @returns {string|int|null} Product attribute value.
 */
function getAttributeValue( attribute ) {
    if( null === attribute.value ) {
        return null;
    } else if( typeof attribute.value === 'object' ) {
        return attribute.value.id;
    } else {
        return attribute.value;
    }
}

/**
 * Check if the attribute or subattribute has a value.
 * 
 * @param attribute
 * @returns {boolean}
 */
function isAttributeValid( attribute ) {
    if( attribute.required ) {
        return !!getAttributeValue( attribute );
    }
    
    return true;
}

/**
 * Update price when the form is changed.
 */
const debouncedCalculate = debounce( updatePrice, 1000 );

watch(
    () => {
        return {
            immediateAttributes: immediateAttributes.value,
            debouncedAttributes: debouncedAttributes.value
        };
    },
    (newVal, oldVal) => {
        if( newVal.immediateAttributes !== oldVal.immediateAttributes ) {
            debouncedCalculate.cancel();
            updatePrice();
        } else {
            debouncedCalculate();
        }
    }
)
</script>

<template>
    <div>
        <Message type="error" :contactUrl="contactUrl" v-if="fatalError">
            <p>{{ fatalError }} Please try again or contact us for help.</p>
        </Message>
        
        <form v-if="!fatalError">
            <OutOfStock :contactUrl="contactUrl" v-if="'outofstock' === product.stockStatus" />
            
            <template v-else>
                <Backordered :contactUrl="contactUrl" v-if="'onbackorder' === product.stockStatus" />
                
                <template v-for="(attribute, index) in product.attributes">
                    <MetreOrRoll v-if="'metre_or_roll' === attribute.type" :id="attribute.id" :label="attribute.label" :options="attribute.options" :subattributes="attribute.subattributes" v-model:value="product.attributes[index].value" v-model:length="product.attributes[index].subattributes.length.value" />
                    <Sides v-else-if="'sides' === attribute.type" :id="attribute.id" :label="attribute.label" :options="attribute.options" :subattributes="attribute.subattributes" v-model:value="product.attributes[index].value" v-model:short-side="product.attributes[index].subattributes['short-side'].value" v-model:long-side="product.attributes[index].subattributes['long-side'].value" />
                    <ColorPicker v-else-if="'colours' === attribute.type" :id="attribute.id" :label="attribute.label" :options="attribute.options" v-model="product.attributes[index].value" />
                    <Select v-else-if="'methods' === attribute.type || 'select' === attribute.type" :id="attribute.id" :label="attribute.label" :options="attribute.options" v-model="product.attributes[index].value" />
                    <ActionWithInput v-else-if="'action_with_input' === attribute.type" :id="attribute.id" :label="attribute.label" :custom="attribute.custom" v-model="product.attributes[index].value" />
                </template>

                <OutOfStock :contactUrl="contactUrl" v-if="'outofstock' === stockStatus" />

                <div class="field field--price" v-else-if="( priceExVatFormatted && priceIncVatFormatted ) || !error">
                    <p class="backordered-message" v-if="'onbackorder' === stockStatus"><strong>This item is currently on backorder.</strong> Before ordering, please be aware of extended shipping timelines for orders containing this product.</p>
                    
                    <template v-if="priceExVatFormatted && priceIncVatFormatted">
                        <div class="price ex-vat">
                            <span class="value" v-html="priceExVatFormatted"></span>
                            <span class="label"> ex. VAT</span>
                        </div>

                        <div class="price inc-vat">
                            <span class="value" v-html="priceIncVatFormatted"></span>
                            <span class="label"> inc. VAT</span>
                        </div>
                    </template>

                    <template v-else-if="!error">
                        <p class="price-placeholder">Select product options to see your price.</p>
                    </template>
                    
                    <div :class="`loader ${loading ? 'active' : ''}`" aria-label="Loading...">
                        <div class="spinner-container">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256"><rect width="256" height="256" fill="none"/><line x1="128" y1="32" x2="128" y2="64" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/><line x1="195.88" y1="60.12" x2="173.25" y2="82.75" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/><line x1="224" y1="128" x2="192" y2="128" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/><line x1="195.88" y1="195.88" x2="173.25" y2="173.25" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/><line x1="128" y1="224" x2="128" y2="192" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/><line x1="60.12" y1="195.88" x2="82.75" y2="173.25" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/><line x1="32" y1="128" x2="64" y2="128" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/><line x1="60.12" y1="60.12" x2="82.75" y2="82.75" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/></svg>
                        </div>
                    </div>
                </div>

                <Quantity id="quantity" label="Quantity" min="1" step="1" max="999" v-model="quantity" v-if="!hideQuantity" :unit="product.quantityLabel" />

                <div class="field field--submit">
                    <button type="submit" @click.prevent="addToCartButtonHandler()" class="whippet-button style--filled color--orange" :disabled="!( priceIncVatFormatted && priceExVatFormatted ) || error || 'outofstock' === stockStatus">Add to basket</button>
                </div>
                
                <Message type="error" :contactUrl="contactUrl" v-if="error">
                    <p>{{ error }}</p>
                </Message>

                <dialog open class="confirmation" v-if="confirmation">
                    <div class="message" ref="confirmationMessage">
                        <p>Please confirm you have entered all the details accurately.</p>
                    </div>

                    <div class="buttons">
                        <button type="submit" @click.prevent="addToCartButtonHandler( true )" class="whippet-button style--filled color--orange">Continue to basket</button>

                        <button type="button" @click.prevent="confirmation = false" class="whippet-button style--text color--navy">Go back and check</button>
                    </div>
                </dialog>
            </template>
        </form>
    </div>
</template>
